/**
 * FrameFlowEngine - WebCodecs-based video processing
 *
 * Single-pass video processing engine that parses FFmpeg-style filter_complex
 * and executes operations using WebCodecs for hardware acceleration.
 */

// Import MP4Box and DataStream from mp4box
// DataStream is exported by mp4box at line 879 of mp4box.all.js
import * as MP4BoxModule from 'mp4box';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

// Get the createFile function and DataStream from the module
const MP4Box = { createFile: MP4BoxModule.createFile };
const DataStream = MP4BoxModule.DataStream;
console.log('[FrameFlow] MP4Box module loaded, DataStream available:', !!DataStream);

// ============================================================================
// Core Detection
// ============================================================================

function isSupported() {
  return typeof VideoDecoder !== 'undefined' &&
         typeof VideoEncoder !== 'undefined' &&
         typeof VideoFrame !== 'undefined' &&
         typeof AudioDecoder !== 'undefined';
}

// ============================================================================
// Metadata Extraction
// ============================================================================

function getMetadata(bytes) {
  return new Promise((resolve, reject) => {
    const mp4box = MP4Box.createFile();

    mp4box.onError = (e) => {
      reject(new Error('MP4 parse error: ' + e));
    };

    mp4box.onReady = (info) => {
      try {
        const videoTrack = info.tracks.find(t => t.type === 'video');
        const audioTrack = info.tracks.find(t => t.type === 'audio');

        if (!videoTrack) {
          reject(new Error('No video track found'));
          return;
        }

        // Calculate frame rate
        const duration = info.duration / info.timescale;
        const frameRate = videoTrack.nb_samples / duration;

        // Extract rotation from transformation matrix
        const rotation = videoTrack.matrix ?
          getRotationFromMatrix(videoTrack.matrix) : 0;

        resolve({
          width: videoTrack.track_width,
          height: videoTrack.track_height,
          duration: duration,
          frameRate: frameRate,
          rotation: rotation,
          videoCodec: videoTrack.codec,
          audioCodec: audioTrack ? audioTrack.codec : null,
        });
      } catch (err) {
        reject(new Error('Failed to extract metadata: ' + err.message));
      }
    };

    // Feed bytes to mp4box
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    buffer.fileStart = 0;

    mp4box.appendBuffer(buffer);
    mp4box.flush();
  });
}

function getRotationFromMatrix(matrix) {
  if (!matrix || matrix.length < 6) return 0;
  const a = matrix[0] / 65536;
  const b = matrix[1] / 65536;
  const radians = Math.atan2(b, a);
  const degrees = Math.round(radians * 180 / Math.PI);
  return ((degrees % 360) + 360) % 360;
}

// ============================================================================
// FFmpeg Args Parser
// ============================================================================

/**
 * Parse FFmpeg-style command line args into structured config.
 *
 * Expected format:
 * -noautorotate -ss 1.0 -t 24.0 -i input_0.mp4
 * -noautorotate -ss 0.5 -t 24.0 -i input_1.mp4
 * -i watermark.png
 * -filter_complex "..."
 * -map [out] -map 0:a?
 * -c:v libx264 ...
 * output.mp4
 */
function parseFFmpegArgs(args) {
  const config = {
    inputs: [],
    filterComplex: '',
    outputCodec: 'libx264',
    outputCrf: 23,
    outputFps: null,
    outputFileName: 'output.mp4',
  };

  let currentInput = null;
  let i = 0;

  while (i < args.length) {
    const arg = args[i];

    if (arg === '-noautorotate') {
      // Start of new input section
      if (currentInput && currentInput.filename) {
        config.inputs.push(currentInput);
      }
      currentInput = { seek: 0, duration: null, filename: null };
      i++;
    } else if (arg === '-ss') {
      if (currentInput) {
        currentInput.seek = parseFloat(args[++i]);
      }
      i++;
    } else if (arg === '-t') {
      if (currentInput) {
        currentInput.duration = parseFloat(args[++i]);
      }
      i++;
    } else if (arg === '-i') {
      const filename = args[++i];
      if (currentInput) {
        currentInput.filename = filename;
        config.inputs.push(currentInput);
        currentInput = null;
      } else {
        // Input without -noautorotate (e.g., watermark)
        config.inputs.push({ seek: 0, duration: null, filename });
      }
      i++;
    } else if (arg === '-filter_complex') {
      config.filterComplex = args[++i];
      i++;
    } else if (arg === '-c:v') {
      config.outputCodec = args[++i];
      i++;
    } else if (arg === '-crf') {
      config.outputCrf = parseInt(args[++i], 10);
      i++;
    } else if (arg === '-r') {
      config.outputFps = parseFloat(args[++i]);
      i++;
    } else if (!arg.startsWith('-') && i === args.length - 1) {
      // Last non-flag argument is output filename
      config.outputFileName = arg;
      i++;
    } else {
      // Skip other flags
      i++;
    }
  }

  // Push any remaining input
  if (currentInput && currentInput.filename) {
    config.inputs.push(currentInput);
  }

  return config;
}

// ============================================================================
// Filter Complex Parser
// ============================================================================

function parseFilterComplex(filterStr) {
  const operations = [];

  // Split by semicolons
  const chains = filterStr.split(';').map(s => s.trim()).filter(s => s);

  for (const chain of chains) {
    // Match ALL input labels at the start (e.g., [0:v], [tmp0][tmp1])
    // The ^ anchor with g flag doesn't work for multiple matches,
    // so we need to use a different approach
    const inputLabelsMatch = chain.match(/^(\[[^\]]+\])+/);
    let inputs = [];
    let filterPart = chain;

    if (inputLabelsMatch) {
      // Extract all individual labels from the matched prefix
      const labelPrefix = inputLabelsMatch[0];
      inputs = labelPrefix.match(/\[([^\]]+)\]/g).map(l => l.slice(1, -1));
      // Remove the entire input label prefix
      filterPart = chain.slice(labelPrefix.length);
    }

    // Remove output label from end
    const outputMatch = filterPart.match(/\[([^\]]+)\]$/);
    const output = outputMatch ? outputMatch[1] : null;
    if (outputMatch) {
      filterPart = filterPart.slice(0, -outputMatch[0].length);
    }

    // Parse filter chain (comma-separated filters)
    const filters = filterPart.split(',').map(f => {
      const eqIdx = f.indexOf('=');
      if (eqIdx === -1) {
        return { name: f.trim(), params: {} };
      }
      const name = f.slice(0, eqIdx).trim();
      const paramsStr = f.slice(eqIdx + 1);
      return { name, params: parseParams(paramsStr) };
    });

    operations.push({ inputs, filters, output });
  }

  return operations;
}

function parseParams(paramsStr) {
  const params = {};
  const parts = paramsStr.split(':');
  parts.forEach((part, i) => {
    if (part.includes('=')) {
      const [k, v] = part.split('=');
      params[k] = parseValue(v);
    } else {
      params[`_${i}`] = parseValue(part);
    }
  });
  return params;
}

function parseValue(v) {
  if (v === undefined) return true;
  if (!isNaN(Number(v))) return Number(v);
  return v;
}

// ============================================================================
// Demuxer - Extract samples from MP4
// ============================================================================

/**
 * Demux an MP4 file and extract video samples (and optionally audio).
 * Returns { info, videoTrack, samples, codecConfig, audioTrack?, audioSamples?, audioCodecConfig? }
 */
function demuxMP4(bytes, extractAudio = false) {
  return new Promise((resolve, reject) => {
    const mp4box = MP4Box.createFile();
    const videoSamples = [];
    const audioSamples = [];
    let videoTrack = null;
    let audioTrack = null;
    let info = null;

    mp4box.onError = (e) => {
      console.error('[demuxMP4] onError:', e);
      reject(new Error('MP4 demux error: ' + e));
    };

    mp4box.onReady = (fileInfo) => {
      info = fileInfo;
      videoTrack = info.tracks.find(t => t.type === 'video');
      audioTrack = info.tracks.find(t => t.type === 'audio');

      if (!videoTrack) {
        reject(new Error('No video track found'));
        return;
      }

      console.log('[demuxMP4] Video track:', videoTrack.codec, `${videoTrack.nb_samples} samples`);

      // Set extraction options for video track - get all samples in one callback
      mp4box.setExtractionOptions(videoTrack.id, 'video', {
        nbSamples: Infinity,
      });

      // Also extract audio if requested and available
      if (extractAudio && audioTrack) {
        console.log('[demuxMP4] Audio track:', audioTrack.codec, `${audioTrack.nb_samples} samples`);
        mp4box.setExtractionOptions(audioTrack.id, 'audio', {
          nbSamples: Infinity,
        });
      }

      mp4box.start();
    };

    mp4box.onSamples = (trackId, ref, sampleArray) => {
      if (ref === 'video') {
        console.log(`[demuxMP4] Received ${sampleArray.length} video samples`);
        videoSamples.push(...sampleArray);
      } else if (ref === 'audio') {
        console.log(`[demuxMP4] Received ${sampleArray.length} audio samples`);
        audioSamples.push(...sampleArray);
      } else {
        // Fallback for single-track case (backwards compatibility)
        console.log(`[demuxMP4] Received ${sampleArray.length} samples (track ${trackId})`);
        if (videoTrack && trackId === videoTrack.id) {
          videoSamples.push(...sampleArray);
        } else if (audioTrack && trackId === audioTrack.id) {
          audioSamples.push(...sampleArray);
        }
      }
    };

    // Feed bytes to mp4box
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    buffer.fileStart = 0;

    mp4box.appendBuffer(buffer);
    mp4box.flush();

    // After flush(), all samples should have been delivered via onSamples
    // Use setTimeout to ensure callbacks have fired (they're synchronous but let's be safe)
    setTimeout(() => {
      if (!videoTrack) {
        reject(new Error('No video track found after parsing'));
        return;
      }

      if (videoSamples.length === 0) {
        reject(new Error('No video samples extracted'));
        return;
      }

      // Pass mp4box instance to buildCodecConfig so it can access full track data
      const codecConfig = buildCodecConfig(videoTrack, mp4box);
      console.log(`[demuxMP4] Complete: ${videoSamples.length} video samples, codec=${codecConfig.codec}`);

      const result = {
        info,
        videoTrack,
        samples: videoSamples, // Keep 'samples' for backwards compatibility
        codecConfig,
      };

      // Add audio if extracted
      if (extractAudio && audioTrack && audioSamples.length > 0) {
        result.audioTrack = audioTrack;
        result.audioSamples = audioSamples;
        result.audioCodecConfig = buildAudioCodecConfig(audioTrack, mp4box);
        console.log(`[demuxMP4] Audio: ${audioSamples.length} samples, codec=${result.audioCodecConfig?.codec}`);
      }

      resolve(result);
    }, 0);
  });
}

/**
 * Build WebCodecs-compatible codec config from MP4 track info.
 * @param {Object} track - Track info from MP4Box onReady
 * @param {Object} mp4box - The MP4Box file instance (needed to access full track data)
 */
function buildCodecConfig(track, mp4box) {
  // Debug: show all dimension options
  console.log('[buildCodecConfig] Track dimensions:', {
    track_width: track.track_width,
    track_height: track.track_height,
    video_width: track.video?.width,
    video_height: track.video?.height,
    width: track.width,
    height: track.height,
  });

  // Use actual coded dimensions for the decoder (video.width/height from stsd box)
  // These are the actual pixel dimensions the encoder used
  const config = {
    // Normalize VP8 codec string
    codec: track.codec.startsWith('vp08') ? 'vp8' : track.codec,
    codedWidth: track.video ? track.video.width : track.track_width,
    codedHeight: track.video ? track.video.height : track.track_height,
    // Store display dimensions for reference (from tkhd box)
    displayWidth: track.track_width,
    displayHeight: track.track_height,
  };

  // Extract codec-specific description from stsd entries
  // Following W3C WebCodecs sample: https://w3c.github.io/webcodecs/samples/video-decode-display/
  // Reference: https://github.com/vjeux/mp4-h264-re-encode
  try {
    const trak = mp4box.getTrackById(track.id);
    console.log('[buildCodecConfig] Track ID:', track.id, 'trak exists:', !!trak);

    if (!trak) {
      console.error('[buildCodecConfig] getTrackById returned null/undefined!');
      // Try fallback: access moov.traks directly
      if (mp4box.moov && mp4box.moov.traks) {
        console.log('[buildCodecConfig] Trying fallback via mp4box.moov.traks');
        const fallbackTrak = mp4box.moov.traks.find(t => t.tkhd && t.tkhd.track_id === track.id);
        if (fallbackTrak) {
          return buildCodecConfigFromTrak(fallbackTrak, config);
        }
      }
      return config;
    }

    return buildCodecConfigFromTrak(trak, config);
  } catch (e) {
    console.error('[buildCodecConfig] Error extracting description:', e);
    // Some codecs (VP8, VP9 in WebM) don't need description
  }

  return config;
}

/**
 * Extract codec description from a trak box.
 */
function buildCodecConfigFromTrak(trak, config) {
  try {
    console.log('[buildCodecConfigFromTrak] trak structure:', {
      hasMdia: !!trak.mdia,
      hasMinf: !!trak.mdia?.minf,
      hasStbl: !!trak.mdia?.minf?.stbl,
      hasStsd: !!trak.mdia?.minf?.stbl?.stsd,
    });

    const stsd = trak.mdia?.minf?.stbl?.stsd;
    if (!stsd) {
      console.error('[buildCodecConfigFromTrak] Could not navigate to stsd');
      return config;
    }

    console.log('[buildCodecConfigFromTrak] stsd object:', {
      type: stsd.type,
      size: stsd.size,
      hasEntries: !!stsd.entries,
      entriesLength: stsd.entries?.length,
      allKeys: Object.getOwnPropertyNames(stsd).slice(0, 20),
    });

    const entries = stsd.entries;
    if (!entries || entries.length === 0) {
      console.error('[buildCodecConfigFromTrak] No entries in stsd');
      return config;
    }

    console.log('[buildCodecConfigFromTrak] Found', entries.length, 'stsd entries');

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];

      if (!entry) {
        console.log(`[buildCodecConfigFromTrak] Entry ${i} is null/undefined`);
        continue;
      }

      console.log(`[buildCodecConfigFromTrak] Entry ${i}:`, {
        type: entry.type,
        size: entry.size,
        constructor: entry.constructor?.name,
      });

      // Log all properties including non-enumerable ones
      const allProps = Object.getOwnPropertyNames(entry);
      console.log(`[buildCodecConfigFromTrak] Entry ${i} own props (${allProps.length}):`, allProps.slice(0, 30).join(', '));

      // Also check prototype properties
      const protoProps = Object.getOwnPropertyNames(Object.getPrototypeOf(entry) || {});
      console.log(`[buildCodecConfigFromTrak] Entry ${i} proto props:`, protoProps.slice(0, 20).join(', '));

      // Check for specific codec boxes
      console.log(`[buildCodecConfigFromTrak] Entry ${i} codec boxes:`, {
        hasAvcC: 'avcC' in entry,
        avcC: entry.avcC ? 'exists' : 'undefined',
        hasHvcC: 'hvcC' in entry,
        hvcC: entry.hvcC ? 'exists' : 'undefined',
        hasVpcC: 'vpcC' in entry,
        hasAv1C: 'av1C' in entry,
        hasBoxes: 'boxes' in entry,
        boxesLength: entry.boxes?.length,
      });

      // Try direct access (Object.keys may not show prototype properties)
      let box = null;
      if (entry.avcC) {
        box = entry.avcC;
        console.log('[buildCodecConfigFromTrak] Found avcC directly on entry');
      } else if (entry.hvcC) {
        box = entry.hvcC;
        console.log('[buildCodecConfigFromTrak] Found hvcC directly on entry');
      } else if (entry.vpcC) {
        box = entry.vpcC;
        console.log('[buildCodecConfigFromTrak] Found vpcC directly on entry');
      } else if (entry.av1C) {
        box = entry.av1C;
        console.log('[buildCodecConfigFromTrak] Found av1C directly on entry');
      }

      // Try boxes array if not found directly
      if (!box && entry.boxes && Array.isArray(entry.boxes)) {
        console.log('[buildCodecConfigFromTrak] Searching in entry.boxes array, length:', entry.boxes.length);
        for (const b of entry.boxes) {
          console.log('[buildCodecConfigFromTrak] Box in array:', b?.type);
          if (b.type === 'avcC' || b.type === 'hvcC' || b.type === 'vpcC' || b.type === 'av1C') {
            box = b;
            console.log('[buildCodecConfigFromTrak] Found box in boxes array:', b.type);
            break;
          }
        }
      }

      if (box) {
        console.log('[buildCodecConfigFromTrak] Serializing box:', {
          type: box.type,
          hasWrite: typeof box.write === 'function',
          size: box.size,
        });

        // Use the box's write method with a DataStream
        // DataStream should be available from mp4box bundle
        try {
          if (typeof DataStream !== 'undefined') {
            console.log('[buildCodecConfigFromTrak] Using global DataStream');
            const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
            box.write(stream);
            console.log('[buildCodecConfigFromTrak] Stream buffer size:', stream.buffer.byteLength);
            // Skip 8 bytes (box header: 4 bytes size + 4 bytes type)
            config.description = new Uint8Array(stream.buffer, 8);
            console.log(`[buildCodecConfigFromTrak] Description: ${config.description.length} bytes`);
          } else if (typeof window !== 'undefined' && window.DataStream) {
            console.log('[buildCodecConfigFromTrak] Using window.DataStream');
            const stream = new window.DataStream(undefined, 0, window.DataStream.BIG_ENDIAN);
            box.write(stream);
            config.description = new Uint8Array(stream.buffer, 8);
            console.log(`[buildCodecConfigFromTrak] Description: ${config.description.length} bytes`);
          } else {
            console.error('[buildCodecConfigFromTrak] DataStream not available!');
            // Fallback: Create a simple DataStream-like object
            const buffer = new ArrayBuffer(1024);
            const view = new DataView(buffer);
            const simpleStream = {
              buffer,
              position: 0,
              endianness: false, // BIG_ENDIAN
              writeUint8: function(v) { view.setUint8(this.position++, v); },
              writeUint16: function(v) { view.setUint16(this.position, v, false); this.position += 2; },
              writeUint32: function(v) { view.setUint32(this.position, v, false); this.position += 4; },
              writeUint8Array: function(arr) { for (let i = 0; i < arr.length; i++) view.setUint8(this.position++, arr[i]); },
              writeString: function(s, enc, len) { for (let i = 0; i < s.length; i++) view.setUint8(this.position++, s.charCodeAt(i)); },
            };
            try {
              box.write(simpleStream);
              config.description = new Uint8Array(buffer.slice(8, simpleStream.position));
              console.log(`[buildCodecConfigFromTrak] Description (fallback): ${config.description.length} bytes`);
            } catch (writeErr) {
              console.error('[buildCodecConfigFromTrak] Fallback write failed:', writeErr);
            }
          }
        } catch (streamErr) {
          console.error('[buildCodecConfigFromTrak] Error creating stream:', streamErr);
        }
        break;
      }
    }

    if (!config.description) {
      console.warn('[buildCodecConfigFromTrak] No codec box found in entries!');
      // Log full entry structure for debugging
      if (entries[0]) {
        try {
          console.log('[buildCodecConfigFromTrak] First entry full dump:', JSON.stringify(entries[0], (k, v) => {
            if (v instanceof ArrayBuffer || v instanceof Uint8Array) return `[Buffer ${v.byteLength || v.length}]`;
            if (typeof v === 'function') return '[Function]';
            if (v === undefined) return '[undefined]';
            return v;
          }, 2).slice(0, 3000));
        } catch (jsonErr) {
          console.log('[buildCodecConfigFromTrak] Could not stringify entry:', jsonErr.message);
          // Log key-value pairs manually
          for (const key of Object.getOwnPropertyNames(entries[0]).slice(0, 30)) {
            const val = entries[0][key];
            console.log(`  ${key}:`, typeof val, val?.type || val?.constructor?.name || val);
          }
        }
      }
    }
  } catch (e) {
    console.error('[buildCodecConfigFromTrak] Error:', e);
  }

  return config;
}

/**
 * Build WebCodecs-compatible audio codec config from MP4 track info.
 * @param {Object} track - Audio track info from MP4Box onReady
 * @param {Object} mp4box - The MP4Box file instance
 * @returns {Object} Audio codec config for AudioDecoder/muxer
 */
function buildAudioCodecConfig(track, mp4box) {
  console.log('[buildAudioCodecConfig] Track:', {
    codec: track.codec,
    timescale: track.timescale,
    audio: track.audio,
  });

  // Common audio codecs in MP4: mp4a.40.2 (AAC-LC), mp4a.40.5 (HE-AAC), opus, etc.
  const config = {
    codec: track.codec,
    sampleRate: track.audio?.sample_rate || track.timescale,
    numberOfChannels: track.audio?.channel_count || 2,
  };

  // Try to extract AudioSpecificConfig (esds box for AAC) or codec-specific data
  try {
    const trak = mp4box.getTrackById(track.id);
    if (trak) {
      const stsd = trak.mdia?.minf?.stbl?.stsd;
      if (stsd && stsd.entries && stsd.entries.length > 0) {
        const entry = stsd.entries[0];

        // For AAC, look for esds box which contains AudioSpecificConfig
        if (entry.esds) {
          // esds contains DecoderConfigDescriptor with AudioSpecificConfig
          const esds = entry.esds;
          if (esds.esd && esds.esd.descs) {
            for (const desc of esds.esd.descs) {
              // DecoderConfigDescriptor (tag 4) contains DecoderSpecificInfo (tag 5)
              if (desc.tag === 5 && desc.data) {
                config.description = new Uint8Array(desc.data);
                console.log(`[buildAudioCodecConfig] Got AudioSpecificConfig: ${config.description.length} bytes`);
                break;
              }
              // Also check nested descs
              if (desc.descs) {
                for (const nested of desc.descs) {
                  if (nested.tag === 5 && nested.data) {
                    config.description = new Uint8Array(nested.data);
                    console.log(`[buildAudioCodecConfig] Got nested AudioSpecificConfig: ${config.description.length} bytes`);
                    break;
                  }
                }
              }
            }
          }
        }

        // For Opus, look for dOps box
        if (entry.dOps) {
          const dOps = entry.dOps;
          // Create Opus identification header from dOps
          // See: https://opus-codec.org/docs/opus_in_isobmff.html
          if (typeof DataStream !== 'undefined') {
            const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
            dOps.write(stream);
            config.description = new Uint8Array(stream.buffer, 8);
            console.log(`[buildAudioCodecConfig] Got Opus config: ${config.description.length} bytes`);
          }
        }
      }
    }
  } catch (e) {
    console.error('[buildAudioCodecConfig] Error extracting description:', e);
  }

  console.log('[buildAudioCodecConfig] Final config:', config);
  return config;
}

// ============================================================================
// Decoder - Decode video samples to VideoFrames
// ============================================================================

/**
 * Decode video samples to VideoFrames.
 *
 * @param {Object} demuxResult - Result from demuxMP4
 * @param {number} seekSeconds - Start time in seconds
 * @param {number|null} durationSeconds - Max duration to decode
 * @param {Function} onFrame - Callback for each decoded frame
 * @returns {Promise<void>}
 */
async function decodeVideo(demuxResult, seekSeconds, durationSeconds, onFrame) {
  const { videoTrack, samples, codecConfig } = demuxResult;

  // Calculate time bounds
  const timescale = videoTrack.timescale;
  const startTimestamp = Math.floor(seekSeconds * timescale);
  const endTimestamp = durationSeconds != null
    ? Math.floor((seekSeconds + durationSeconds) * timescale)
    : Infinity;

  // Find the nearest keyframe at or before startTimestamp
  // VideoDecoder requires a keyframe after configure()
  let keyframeIndex = -1;
  for (let i = 0; i < samples.length; i++) {
    if (samples[i].is_sync && samples[i].dts <= startTimestamp) {
      keyframeIndex = i;
    } else if (samples[i].dts > startTimestamp) {
      break; // Past our start point, stop searching
    }
  }

  // If no keyframe found before start, use the first keyframe
  if (keyframeIndex === -1) {
    keyframeIndex = samples.findIndex(s => s.is_sync);
    if (keyframeIndex === -1) {
      console.error('[decodeVideo] No keyframes found in video');
      return;
    }
  }

  // Filter samples: start from keyframe, end at endTimestamp
  // We decode from keyframe but only emit frames within requested range
  const relevantSamples = samples.filter((sample, i) => {
    if (i < keyframeIndex) return false; // Before our keyframe
    return sample.dts < endTimestamp;
  });

  if (relevantSamples.length === 0) {
    return;
  }

  console.log(`[decodeVideo] Decoding ${relevantSamples.length} samples (keyframe at index ${keyframeIndex}, dts=${samples[keyframeIndex].dts})`);
  console.log(`[decodeVideo] Requested range: ${startTimestamp}-${endTimestamp} (timescale=${timescale})`);
  console.log(`[decodeVideo] Codec config:`, JSON.stringify({
    codec: codecConfig.codec,
    codedWidth: codecConfig.codedWidth,
    codedHeight: codecConfig.codedHeight,
    hasDescription: !!codecConfig.description,
    descriptionLength: codecConfig.description?.length,
  }));

  // Check if codec is supported
  try {
    const support = await VideoDecoder.isConfigSupported(codecConfig);
    console.log(`[decodeVideo] Codec support:`, support.supported ? 'YES' : 'NO');
    if (!support.supported) {
      throw new Error(`Codec not supported: ${codecConfig.codec}`);
    }
  } catch (e) {
    console.error('[decodeVideo] isConfigSupported failed:', e);
    // Continue anyway - some browsers don't implement this well
  }

  // Calculate time bounds for filtering frames early
  const startMicroseconds = seekSeconds * 1000000;
  const endMicroseconds = durationSeconds != null
    ? (seekSeconds + durationSeconds) * 1000000
    : Infinity;

  // Create decoder with error tracking and early frame filtering
  const frames = [];
  let decodeError = null;
  let droppedCount = 0;

  const decoder = new VideoDecoder({
    output: (frame) => {
      // Immediately close frames outside the requested time range to save memory
      if (frame.timestamp < startMicroseconds || frame.timestamp >= endMicroseconds) {
        frame.close();
        droppedCount++;
      } else {
        frames.push(frame);
      }
    },
    error: (e) => {
      console.error('[decodeVideo] Decoder error:', e?.message || e);
      decodeError = e;
    },
  });

  // Configure decoder
  try {
    decoder.configure(codecConfig);
  } catch (e) {
    console.error('[decodeVideo] configure() threw:', e);
    throw new Error(`Decoder configuration failed: ${e?.message || e}`);
  }

  // Decode samples with flow control and retry logic
  const MAX_QUEUE_SIZE = 10;
  const BATCH_SIZE = 30;
  const MAX_RETRIES = 3;
  let retryCount = 0;
  let lastSuccessIndex = -1;

  async function decodeWithRetry() {
    for (let i = 0; i < relevantSamples.length; i++) {
      // If we hit an error, try to recover
      if (decodeError) {
        if (retryCount >= MAX_RETRIES) {
          console.error(`[decodeVideo] Max retries (${MAX_RETRIES}) reached, stopping`);
          break;
        }

        retryCount++;
        console.warn(`[decodeVideo] Decoder error at sample ${i}, retry ${retryCount}/${MAX_RETRIES}`);

        // Find next keyframe after current position to resume from
        let nextKeyframe = -1;
        for (let j = i + 1; j < relevantSamples.length; j++) {
          if (relevantSamples[j].is_sync) {
            nextKeyframe = j;
            break;
          }
        }

        if (nextKeyframe === -1) {
          console.warn('[decodeVideo] No more keyframes to resume from');
          break;
        }

        // Reset decoder state
        decodeError = null;
        try {
          decoder.reset();
          decoder.configure(codecConfig);
        } catch (e) {
          console.error('[decodeVideo] Failed to reset decoder:', e);
          break;
        }

        // Skip to next keyframe
        console.log(`[decodeVideo] Resuming from keyframe at sample ${nextKeyframe}`);
        i = nextKeyframe - 1; // -1 because loop will increment
        continue;
      }

      const sample = relevantSamples[i];
      const chunk = new EncodedVideoChunk({
        type: sample.is_sync ? 'key' : 'delta',
        timestamp: Math.floor(sample.cts * 1000000 / timescale),
        duration: Math.floor(sample.duration * 1000000 / timescale),
        data: sample.data,
      });

      try {
        decoder.decode(chunk);
        lastSuccessIndex = i;
      } catch (e) {
        console.error(`[decodeVideo] decode() threw at sample ${i}:`, e);
        // Clean up any frames we've already decoded before throwing
        for (const frame of frames) {
          frame.close();
        }
        frames.length = 0;
        throw new Error(`Decoding failed: ${e?.message || e}`);
      }

      // Flow control: wait for decoder to drain if queue is too large
      if (decoder.decodeQueueSize > MAX_QUEUE_SIZE || (i > 0 && i % BATCH_SIZE === 0)) {
        await new Promise(resolve => setTimeout(resolve, 0));
        while (decoder.decodeQueueSize > MAX_QUEUE_SIZE && !decodeError) {
          await new Promise(resolve => setTimeout(resolve, 5));
        }
      }
    }
  }

  await decodeWithRetry();

  // Flush decoder
  try {
    await decoder.flush();
  } catch (e) {
    console.error('[decodeVideo] flush() threw:', e);
    if (frames.length === 0) {
      decoder.close();
      throw new Error(`Decoder flush failed: ${e?.message || e}`);
    }
    console.warn(`[decodeVideo] flush() failed but we have ${frames.length} frames, continuing`);
  }
  decoder.close();

  // Check if async error occurred (but allow if we have some frames)
  if (decodeError && frames.length === 0) {
    throw new Error(`Decoding error: ${decodeError?.message || decodeError}`);
  }
  if (decodeError) {
    console.warn(`[decodeVideo] Decoder error occurred but we have ${frames.length} frames, continuing`);
  }

  // Call onFrame for each decoded frame in order
  // Frames are already filtered in the output callback
  frames.sort((a, b) => a.timestamp - b.timestamp);

  for (const frame of frames) {
    // Pass frame to callback - caller owns the frame now and is responsible for closing it
    await onFrame(frame);
  }
  console.log(`[decodeVideo] Emitted ${frames.length} frames (dropped ${droppedCount} outside range)`);
}

/**
 * Decode a chunk of video frames, returning them as an array.
 * Wrapper around decodeVideo() for chunked processing.
 *
 * @param {Object} demuxResult - Result from demuxMP4
 * @param {number} startSeconds - Start time in seconds
 * @param {number} endSeconds - End time in seconds
 * @returns {Promise<VideoFrame[]>} - Array of VideoFrames sorted by timestamp
 */
async function decodeVideoChunk(demuxResult, startSeconds, endSeconds) {
  const frames = [];
  const duration = endSeconds - startSeconds;
  await decodeVideo(demuxResult, startSeconds, duration, frame => {
    frames.push(frame);
  });
  // Frames should already be sorted, but ensure it
  frames.sort((a, b) => a.timestamp - b.timestamp);
  return frames;
}

/**
 * Find the frame with timestamp closest to the target.
 * Uses binary search for efficiency on sorted frame arrays.
 *
 * @param {VideoFrame[]} frames - Array of VideoFrames sorted by timestamp
 * @param {number} targetTimestamp - Target timestamp in microseconds
 * @returns {VideoFrame|null} - Closest frame or null if array is empty
 */
function findClosestFrame(frames, targetTimestamp) {
  if (frames.length === 0) return null;
  if (frames.length === 1) return frames[0];

  // Binary search for closest timestamp
  let left = 0;
  let right = frames.length - 1;

  while (left < right - 1) {
    const mid = Math.floor((left + right) / 2);
    if (frames[mid].timestamp <= targetTimestamp) {
      left = mid;
    } else {
      right = mid;
    }
  }

  // Compare left and right to find closest
  const leftDiff = Math.abs(frames[left].timestamp - targetTimestamp);
  const rightDiff = Math.abs(frames[right].timestamp - targetTimestamp);
  return leftDiff <= rightDiff ? frames[left] : frames[right];
}

// ============================================================================
// Canvas Operations
// ============================================================================

function scaleFrame(frame, targetWidth, targetHeight, canvas) {
  // ALWAYS use codedWidth/codedHeight for source dimensions - these are the actual pixels
  // displayWidth/displayHeight can be wrong/mismatched and cause tiny rendering
  const srcWidth = frame.codedWidth;
  const srcHeight = frame.codedHeight;

  if (targetWidth < 0 || targetWidth === -2) {
    targetWidth = Math.round(srcWidth * targetHeight / srcHeight);
    if (targetWidth % 2 !== 0) targetWidth += 1;
  }
  if (targetHeight < 0 || targetHeight === -2) {
    targetHeight = Math.round(srcHeight * targetWidth / srcWidth);
    if (targetHeight % 2 !== 0) targetHeight += 1;
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  // Use 9-arg form to explicitly specify source rectangle as FULL coded dimensions
  // This ensures we take ALL pixels, not just displayWidth x displayHeight
  ctx.drawImage(frame,
    0, 0, srcWidth, srcHeight,      // source: full coded pixels
    0, 0, targetWidth, targetHeight  // destination: scaled
  );
  return new VideoFrame(canvas, {
    timestamp: frame.timestamp,
    duration: frame.duration,
    displayWidth: targetWidth,
    displayHeight: targetHeight,
  });
}

function cropFrame(frame, width, height, x, y, canvas) {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // Use 9-arg form: crop from (x, y) with size (width, height) from source coded pixels
  ctx.drawImage(frame, x, y, width, height, 0, 0, width, height);
  return new VideoFrame(canvas, {
    timestamp: frame.timestamp,
    duration: frame.duration,
    displayWidth: width,
    displayHeight: height,
  });
}

function flipHorizontal(frame, canvas) {
  // Use codedWidth/codedHeight for actual pixel dimensions
  const w = frame.codedWidth;
  const h = frame.codedHeight;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.translate(w, 0);
  ctx.scale(-1, 1);
  // Use 9-arg form to explicitly read all coded pixels
  ctx.drawImage(frame, 0, 0, w, h, 0, 0, w, h);
  return new VideoFrame(canvas, {
    timestamp: frame.timestamp,
    duration: frame.duration,
    displayWidth: w,
    displayHeight: h,
  });
}

function flipVertical(frame, canvas) {
  // Use codedWidth/codedHeight for actual pixel dimensions
  const w = frame.codedWidth;
  const h = frame.codedHeight;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.translate(0, h);
  ctx.scale(1, -1);
  // Use 9-arg form to explicitly read all coded pixels
  ctx.drawImage(frame, 0, 0, w, h, 0, 0, w, h);
  return new VideoFrame(canvas, {
    timestamp: frame.timestamp,
    duration: frame.duration,
    displayWidth: w,
    displayHeight: h,
  });
}

function transpose(frame, direction, canvas) {
  // Use codedWidth/codedHeight for actual pixel dimensions
  const w = frame.codedWidth;
  const h = frame.codedHeight;
  canvas.width = h;
  canvas.height = w;
  const ctx = canvas.getContext('2d');

  if (direction === 1) {
    ctx.translate(h, 0);
    ctx.rotate(Math.PI / 2);
  } else if (direction === 2) {
    ctx.translate(0, w);
    ctx.rotate(-Math.PI / 2);
  }
  // Use 9-arg form to explicitly read all coded pixels
  ctx.drawImage(frame, 0, 0, w, h, 0, 0, w, h);
  // Note: dimensions are swapped after transpose
  return new VideoFrame(canvas, {
    timestamp: frame.timestamp,
    duration: frame.duration,
    displayWidth: h,
    displayHeight: w,
  });
}

/**
 * Stack frames horizontally (side by side).
 * @param {VideoFrame} leftFrame - Left frame
 * @param {VideoFrame} rightFrame - Right frame
 * @param {OffscreenCanvas} canvas - Canvas to draw on
 * @param {Object} options - Optional explicit DESTINATION dimensions
 * @param {number} options.leftWidth - Destination width for left frame
 * @param {number} options.leftHeight - Destination height for left frame
 * @param {number} options.rightWidth - Destination width for right frame
 * @param {number} options.rightHeight - Destination height for right frame
 */
function hstackFrames(leftFrame, rightFrame, canvas, options = {}) {
  // Use explicit DESTINATION dimensions if provided, otherwise use coded dimensions
  const lw = options.leftWidth || leftFrame.codedWidth;
  const lh = options.leftHeight || leftFrame.codedHeight;
  const rw = options.rightWidth || rightFrame.codedWidth;
  const rh = options.rightHeight || rightFrame.codedHeight;

  const outWidth = lw + rw;
  const outHeight = lh;
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext('2d');
  // Use 9-arg drawImage to explicitly specify source as ALL coded pixels
  // This ensures we read the full frame content, not just displayWidth x displayHeight
  ctx.drawImage(leftFrame,
    0, 0, leftFrame.codedWidth, leftFrame.codedHeight,  // source: all coded pixels
    0, 0, lw, lh                                         // dest: target size
  );
  ctx.drawImage(rightFrame,
    0, 0, rightFrame.codedWidth, rightFrame.codedHeight, // source: all coded pixels
    lw, 0, rw, rh                                         // dest: target size
  );
  return new VideoFrame(canvas, {
    timestamp: leftFrame.timestamp,
    duration: leftFrame.duration,
    displayWidth: outWidth,
    displayHeight: outHeight,
  });
}

/**
 * Stack frames vertically (top and bottom).
 * @param {VideoFrame} topFrame - Top frame
 * @param {VideoFrame} bottomFrame - Bottom frame
 * @param {OffscreenCanvas} canvas - Canvas to draw on
 * @param {Object} options - Optional explicit DESTINATION dimensions
 * @param {number} options.topWidth - Destination width for top frame
 * @param {number} options.topHeight - Destination height for top frame
 * @param {number} options.bottomWidth - Destination width for bottom frame
 * @param {number} options.bottomHeight - Destination height for bottom frame
 */
function vstackFrames(topFrame, bottomFrame, canvas, options = {}) {
  // Use explicit DESTINATION dimensions if provided, otherwise use coded dimensions
  const tw = options.topWidth || topFrame.codedWidth;
  const th = options.topHeight || topFrame.codedHeight;
  const bw = options.bottomWidth || bottomFrame.codedWidth;
  const bh = options.bottomHeight || bottomFrame.codedHeight;

  const outWidth = tw;
  const outHeight = th + bh;
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext('2d');
  // Use 9-arg drawImage to explicitly specify source as ALL coded pixels
  ctx.drawImage(topFrame,
    0, 0, topFrame.codedWidth, topFrame.codedHeight,  // source: all coded pixels
    0, 0, tw, th                                       // dest: target size
  );
  ctx.drawImage(bottomFrame,
    0, 0, bottomFrame.codedWidth, bottomFrame.codedHeight, // source: all coded pixels
    0, th, bw, bh                                           // dest: target size
  );
  return new VideoFrame(canvas, {
    timestamp: topFrame.timestamp,
    duration: topFrame.duration,
    displayWidth: outWidth,
    displayHeight: outHeight,
  });
}

function overlayFrame(baseFrame, overlayImage, x, y, canvas) {
  // Use codedWidth/codedHeight for actual pixel dimensions
  const w = baseFrame.codedWidth;
  const h = baseFrame.codedHeight;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // Use 9-arg drawImage to explicitly read all coded pixels
  ctx.drawImage(baseFrame, 0, 0, w, h, 0, 0, w, h);

  // Parse position expressions (e.g., "main_h-overlay_h-24")
  let posX = x;
  let posY = y;

  if (typeof x === 'string') {
    posX = evalPositionExpr(x, w, h, overlayImage.width, overlayImage.height);
  }
  if (typeof y === 'string') {
    posY = evalPositionExpr(y, w, h, overlayImage.width, overlayImage.height);
  }

  ctx.drawImage(overlayImage, posX, posY);
  return new VideoFrame(canvas, {
    timestamp: baseFrame.timestamp,
    duration: baseFrame.duration,
    displayWidth: w,
    displayHeight: h,
  });
}

/**
 * Evaluate FFmpeg overlay position expression.
 * Supports: main_w, main_h, overlay_w, overlay_h, numeric values, +, -
 */
function evalPositionExpr(expr, mainW, mainH, overlayW, overlayH) {
  if (typeof expr === 'number') return expr;

  let result = expr
    .replace(/main_w/g, mainW)
    .replace(/main_h/g, mainH)
    .replace(/overlay_w/g, overlayW)
    .replace(/overlay_h/g, overlayH);

  try {
    // Safe eval using Function constructor (only arithmetic)
    return Function('"use strict"; return (' + result + ')')();
  } catch (e) {
    console.warn('Failed to evaluate position expr:', expr, e);
    return 0;
  }
}

/**
 * Process a single frame pair (left, right) through the filter pipeline.
 * Handles: scale → crop → compose → watermark → encode
 *
 * @param {Object} params - Processing parameters
 * @param {VideoFrame} params.left - Left/top input frame
 * @param {VideoFrame} params.right - Right/bottom input frame
 * @param {number} params.timestamp - Output timestamp in microseconds
 * @param {number} params.frameIndex - Frame index (for keyframe decisions and logging)
 * @param {VideoEncoder} params.encoder - Video encoder instance
 * @param {OffscreenCanvas} params.canvas - Reusable composition canvas
 * @param {Object} params.config - Processing configuration
 * @param {boolean} params.closeSourceFrames - Whether to close left/right after processing
 * @returns {boolean} - True if processed successfully
 */
function processFramePair({
  left, right, timestamp, frameIndex, encoder, canvas,
  config, closeSourceFrames = true
}) {
  const {
    scaledWidth0, scaledWidth1, targetHeight,
    finalWidth0, finalWidth1, cropHeight,
    hasHstack, outputWidth, outputHeight, frameDuration,
    filterOps, scaledWatermark, watermarkX, watermarkY
  } = config;

  // Debug: log first frame dimensions
  if (frameIndex === 0) {
    console.log(`[FrameFlow] Frame 0 left: coded=${left.codedWidth}x${left.codedHeight}, display=${left.displayWidth}x${left.displayHeight}`);
    console.log(`[FrameFlow] Frame 0 right: coded=${right.codedWidth}x${right.codedHeight}, display=${right.displayWidth}x${right.displayHeight}`);
    console.log(`[FrameFlow] Scaling to: left=${scaledWidth0}x${targetHeight}, right=${scaledWidth1}x${targetHeight}`);
  }

  // Apply filters to each frame
  // First: scale to target height
  const tempCanvas = new OffscreenCanvas(scaledWidth0, targetHeight);
  let scaledLeft = scaleFrame(left, scaledWidth0, targetHeight, tempCanvas);

  tempCanvas.width = scaledWidth1;
  let scaledRight = scaleFrame(right, scaledWidth1, targetHeight, tempCanvas);

  // Debug: log scaled frame dimensions for first frame
  if (frameIndex === 0) {
    console.log(`[FrameFlow] Scaled left: ${scaledLeft.displayWidth}x${scaledLeft.displayHeight}`);
    console.log(`[FrameFlow] Scaled right: ${scaledRight.displayWidth}x${scaledRight.displayHeight}`);
  }

  // Find crop operations and apply them
  for (const op of filterOps) {
    for (const filter of op.filters) {
      if (filter.name === 'crop') {
        const cropW = filter.params._0 || filter.params.w;
        const cropH = filter.params._1 || filter.params.h;
        const cropX = filter.params._2 || filter.params.x || 0;
        const cropY = filter.params._3 || filter.params.y || 0;

        // Apply to whichever input this chain belongs to
        const inputLabel = op.inputs[0];
        if (inputLabel && inputLabel.includes('tmp')) {
          // Determine which input based on label index
          const labelNum = parseInt(inputLabel.replace(/[^\d]/g, ''));
          if (labelNum % 2 === 0) {
            // Even labels are left input - close old frame before reassigning
            const oldScaledLeft = scaledLeft;
            scaledLeft = cropFrame(scaledLeft, cropW, cropH, cropX, cropY, tempCanvas);
            oldScaledLeft.close();
          } else {
            // Odd labels are right input - close old frame before reassigning
            const oldScaledRight = scaledRight;
            scaledRight = cropFrame(scaledRight, cropW, cropH, cropX, cropY, tempCanvas);
            oldScaledRight.close();
          }
        }
      }
    }
  }

  // Compose (hstack or vstack)
  // Use finalWidth/Height (post-crop dimensions) not scaledWidth (pre-crop)
  let composedFrame;
  const finalHeight = cropHeight || targetHeight;
  if (hasHstack) {
    composedFrame = hstackFrames(scaledLeft, scaledRight, canvas, {
      leftWidth: finalWidth0,
      leftHeight: finalHeight,
      rightWidth: finalWidth1,
      rightHeight: finalHeight,
    });
  } else {
    composedFrame = vstackFrames(scaledLeft, scaledRight, canvas, {
      topWidth: finalWidth0,
      topHeight: finalHeight,
      bottomWidth: finalWidth1,
      bottomHeight: finalHeight,
    });
  }

  // Debug: log composed dimensions on first frame
  if (frameIndex === 0) {
    console.log(`[FrameFlow] Composed frame: display=${composedFrame.displayWidth}x${composedFrame.displayHeight}, coded=${composedFrame.codedWidth}x${composedFrame.codedHeight}`);
    console.log(`[FrameFlow] Canvas after compose: ${canvas.width}x${canvas.height}`);
    console.log(`[FrameFlow] Target output: ${outputWidth}x${outputHeight}`);
  }

  // Apply pre-scaled watermark if present
  if (scaledWatermark) {
    // Calculate Y position if it was an expression (e.g., 'main_h-overlay_h-24')
    const y = watermarkY !== null ? watermarkY :
      (composedFrame.displayHeight - (scaledWatermark.height || scaledWatermark.displayHeight) - 24);

    const oldFrame = composedFrame;
    composedFrame = overlayFrame(composedFrame, scaledWatermark, watermarkX, y, canvas);
    oldFrame.close();
  }

  // Use the pre-calculated output dimensions (from metadata and scale filter)
  // rather than relying on composed frame dimensions which may be affected by codec config issues
  const outputFrame = new VideoFrame(canvas, {
    timestamp,
    duration: frameDuration,
    displayWidth: outputWidth,
    displayHeight: outputHeight,
  });
  composedFrame.close();

  // Encode
  encoder.encode(outputFrame, { keyFrame: frameIndex % 30 === 0 });
  outputFrame.close();

  // Cleanup intermediate frames
  scaledLeft.close();
  scaledRight.close();

  // Optionally close source frames (caller may want to reuse them)
  if (closeSourceFrames) {
    left.close();
    right.close();
  }

  return true;
}

// ============================================================================
// Encoder - Encode VideoFrames to chunks
// ============================================================================

/**
 * Create an encoder that writes chunks to a buffer.
 *
 * @param {number} width - Output width
 * @param {number} height - Output height
 * @param {number} fps - Frames per second
 * @param {number} crf - Quality (0-51, lower is better)
 * @returns {{ encoder: VideoEncoder, chunks: EncodedVideoChunk[], close: () => Promise<void> }}
 */
async function createEncoder(width, height, fps, crf) {
  const chunks = [];

  const encoder = new VideoEncoder({
    output: (chunk, metadata) => {
      chunks.push({ chunk, metadata });
    },
    error: (e) => {
      console.error('Encoder error:', e);
    },
  });

  // Calculate bitrate from CRF (rough approximation)
  // CRF 23 at 1080p ~= 2500kbps, scale proportionally
  const pixelCount = width * height;
  const refPixels = 1920 * 1080;
  const refBitrate = 2500000; // 2.5 Mbps for CRF 23 at 1080p
  const crfFactor = Math.pow(2, (23 - crf) / 6); // CRF is logarithmic
  const bitrate = Math.round(refBitrate * (pixelCount / refPixels) * crfFactor);

  // Select appropriate H.264 level based on resolution
  // Level limits: 3.0 = 1620 macroblocks, 4.0 = 8192, 4.1 = 8192, 5.0 = 22080
  const macroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
  let codec;
  if (macroblocks <= 1620) {
    codec = 'avc1.42E01E'; // Baseline Level 3.0 (720p)
  } else if (macroblocks <= 8192) {
    codec = 'avc1.4D0028'; // Main Profile Level 4.0 (1080p)
  } else {
    codec = 'avc1.4D0032'; // Main Profile Level 5.0 (4K)
  }
  console.log(`[FrameFlow] Encoder: ${width}x${height}, ${macroblocks} macroblocks, codec=${codec}`);

  const config = {
    codec,
    width,
    height,
    bitrate,
    framerate: fps,
    latencyMode: 'quality',
    avc: { format: 'avc' },
  };

  try {
    await encoder.configure(config);
  } catch (e) {
    console.error('[FrameFlow] Encoder configure failed:', e);
    throw e;
  }

  return {
    encoder,
    chunks,
    close: async () => {
      await encoder.flush();
      encoder.close();
    },
  };
}

// ============================================================================
// Muxer - Package encoded chunks into MP4
// ============================================================================

/**
 * Mux encoded video chunks (and optional audio) into an MP4 file.
 *
 * @param {Array} videoChunks - Array of { chunk, metadata } from encoder
 * @param {number} width - Video width
 * @param {number} height - Video height
 * @param {number} fps - Frames per second
 * @param {Object} audioData - Optional audio data { samples, track, codecConfig, seekSeconds, durationSeconds }
 * @returns {Uint8Array} - MP4 file bytes
 */
function muxToMP4(videoChunks, width, height, fps, audioData = null) {
  const target = new ArrayBufferTarget();

  // Configure muxer with video (and optional audio)
  const muxerConfig = {
    target,
    video: {
      codec: 'avc',
      width,
      height,
    },
    fastStart: 'in-memory',
  };

  // Add audio track config if audio data is provided
  if (audioData && audioData.samples && audioData.samples.length > 0) {
    const { codecConfig } = audioData;
    // Determine audio codec for muxer
    // mp4a.40.2 = AAC-LC, mp4a.40.5 = HE-AAC
    const codecStr = codecConfig.codec || '';
    let audioCodec = 'aac'; // Default to AAC

    if (codecStr.includes('opus') || codecStr.startsWith('Opus')) {
      audioCodec = 'opus';
    } else if (codecStr.includes('mp3') || codecStr.includes('mp4a.69') || codecStr.includes('mp4a.6b')) {
      audioCodec = 'mp3';
    }

    muxerConfig.audio = {
      codec: audioCodec,
      sampleRate: codecConfig.sampleRate || 44100,
      numberOfChannels: codecConfig.numberOfChannels || 2,
    };
    console.log(`[muxToMP4] Audio config: ${audioCodec}, ${muxerConfig.audio.sampleRate}Hz, ${muxerConfig.audio.numberOfChannels}ch`);
  }

  const muxer = new Muxer(muxerConfig);

  // Add video chunks
  for (const { chunk, metadata } of videoChunks) {
    muxer.addVideoChunk(chunk, metadata);
  }

  // Add audio samples if present (passthrough - no re-encoding)
  if (audioData && audioData.samples && audioData.samples.length > 0) {
    const { samples, track, seekSeconds = 0, durationSeconds = null } = audioData;
    const timescale = track.timescale;

    // Calculate time bounds
    const startTimestamp = Math.floor(seekSeconds * timescale);
    const endTimestamp = durationSeconds != null
      ? Math.floor((seekSeconds + durationSeconds) * timescale)
      : Infinity;

    console.log(`[muxToMP4] Adding audio: ${samples.length} samples, seek=${seekSeconds}s, duration=${durationSeconds}s`);

    // Calculate audio timestamp offset to align with video (video starts at 0)
    const audioStartOffset = seekSeconds * 1000000; // Convert to microseconds

    let addedCount = 0;
    for (const sample of samples) {
      // Filter by time range
      if (sample.dts < startTimestamp) continue;
      if (sample.dts >= endTimestamp) break;

      // Calculate timestamp relative to video start
      const originalTimestamp = Math.floor(sample.cts * 1000000 / timescale);
      const adjustedTimestamp = originalTimestamp - audioStartOffset;

      if (adjustedTimestamp < 0) continue; // Skip samples before video start

      // Use addAudioChunkRaw for passthrough (raw encoded data)
      // This avoids needing to decode/re-encode audio
      try {
        muxer.addAudioChunkRaw(
          sample.data,
          sample.is_sync ? 'key' : 'delta',
          adjustedTimestamp,
          Math.floor(sample.duration * 1000000 / timescale)
        );
        addedCount++;
      } catch (e) {
        console.error('[muxToMP4] Error adding audio chunk:', e);
        // Continue with other samples
      }
    }
    console.log(`[muxToMP4] Added ${addedCount} audio samples`);
  }

  muxer.finalize();

  return new Uint8Array(target.buffer);
}

// ============================================================================
// Pipeline Execution
// ============================================================================

let cancelled = false;

/**
 * Simple passthrough pipeline (single input, no filter_complex).
 *
 * Used for:
 * - Identity operation (copy with seek)
 * - Re-encoding without filters
 *
 * @param {Object} config - Parsed FFmpeg config
 * @param {Object} inputFiles - Map of filename -> Uint8Array
 * @param {Function} progressCallback - Progress callback (0-1)
 * @returns {Promise<Uint8Array|null>} - Output video bytes
 */
async function execPassthrough(config, inputFiles, progressCallback) {
  console.log('[FrameFlow] execPassthrough starting...');

  // Get the single video input
  const videoInputs = config.inputs.filter(input =>
    input.filename && !input.filename.includes('watermark')
  );

  if (videoInputs.length !== 1) {
    throw new Error(`Passthrough mode requires exactly 1 video input, got ${videoInputs.length}`);
  }

  const inputConfig = videoInputs[0];
  const inputBytes = inputFiles[inputConfig.filename];
  if (!inputBytes) {
    throw new Error(`Input file not found: ${inputConfig.filename}`);
  }

  // Get seek and duration from input config
  const seekSeconds = inputConfig.seek || 0;
  const durationSeconds = inputConfig.duration || null;

  console.log(`[FrameFlow] Passthrough: seek=${seekSeconds}s, duration=${durationSeconds}s`);

  // Demux input (with audio extraction)
  console.log('[FrameFlow] Demuxing input (with audio)...');
  const demuxResult = await demuxMP4(inputBytes, true); // true = extract audio

  // Get metadata for output dimensions
  const meta = await getMetadata(inputBytes);
  const fps = meta.frameRate || 30;
  const outputWidth = meta.width % 2 === 0 ? meta.width : meta.width + 1;
  const outputHeight = meta.height % 2 === 0 ? meta.height : meta.height + 1;

  console.log(`[FrameFlow] Input: ${meta.width}x${meta.height} @ ${fps}fps, duration=${meta.duration}s`);
  console.log(`[FrameFlow] Output dimensions: ${outputWidth}x${outputHeight}`);

  // Calculate actual duration to process
  const startTime = seekSeconds;
  const endTime = durationSeconds ? seekSeconds + durationSeconds : meta.duration;
  const outputDuration = endTime - startTime;

  console.log(`[FrameFlow] Processing time range: ${startTime}s to ${endTime}s (${outputDuration}s)`);

  // Decode frames within time range
  console.log('[FrameFlow] Decoding frames...');
  const frames = [];
  await decodeVideo(demuxResult, startTime, outputDuration, (frame) => {
    frames.push(frame);
  });

  console.log(`[FrameFlow] Decoded ${frames.length} frames`);

  if (frames.length === 0) {
    throw new Error('No frames decoded from input');
  }

  // Sort frames by timestamp
  frames.sort((a, b) => a.timestamp - b.timestamp);

  // Create encoder
  const crf = config.outputCrf || 23;
  const encoder = await createEncoder(outputWidth, outputHeight, fps, crf);

  // Encode frames
  console.log('[FrameFlow] Encoding frames...');
  const frameDurationMicros = 1000000 / fps;
  let outputTimestamp = 0;

  for (let i = 0; i < frames.length; i++) {
    if (cancelled) {
      console.log('[FrameFlow] Cancelled during encoding');
      frames.forEach(f => f.close());
      encoder.close();
      return null;
    }

    const frame = frames[i];

    // Create output frame with correct timestamp
    const outputFrame = new VideoFrame(frame, {
      timestamp: outputTimestamp,
      duration: frameDurationMicros,
    });

    // Encode
    const keyFrame = i % 30 === 0; // Keyframe every 30 frames
    encoder.encoder.encode(outputFrame, { keyFrame });

    // Cleanup
    outputFrame.close();
    frame.close();

    outputTimestamp += frameDurationMicros;

    // Report progress
    if (progressCallback) {
      progressCallback((i + 1) / frames.length);
    }
  }

  // Finalize encoding
  console.log('[FrameFlow] Finalizing encoding...');
  await encoder.close();

  // Prepare audio data for muxing (if audio was extracted)
  let audioData = null;
  if (demuxResult.audioSamples && demuxResult.audioSamples.length > 0) {
    audioData = {
      samples: demuxResult.audioSamples,
      track: demuxResult.audioTrack,
      codecConfig: demuxResult.audioCodecConfig,
      seekSeconds: seekSeconds,
      durationSeconds: durationSeconds,
    };
    console.log(`[FrameFlow] Will mux ${audioData.samples.length} audio samples`);
  }

  // Mux to MP4 (with optional audio)
  console.log('[FrameFlow] Muxing to MP4...');
  const output = muxToMP4(encoder.chunks, outputWidth, outputHeight, fps, audioData);

  console.log(`[FrameFlow] Done! Output size: ${output.length} bytes`);
  return output;
}

/**
 * Execute video processing pipeline.
 *
 * @param {string[]} args - FFmpeg-style command line arguments
 * @param {Object} inputFiles - Map of filename -> Uint8Array
 * @param {string} outputFileName - Name for output file
 * @param {Function} progressCallback - Progress callback (0-1)
 * @returns {Promise<Uint8Array|null>} - Output video bytes, or null if cancelled
 */
async function exec(args, inputFiles, outputFileName, progressCallback, options = {}) {
  cancelled = false;
  const { chunked = true, chunkSeconds = 4.0 } = options;

  console.log('[FrameFlow] exec() called with', args.length, 'args');
  console.log('[FrameFlow] Input files:', Object.keys(inputFiles || {}));

  // Parse FFmpeg args
  const config = parseFFmpegArgs(args);
  console.log('[FrameFlow] Parsed config:', JSON.stringify(config, null, 2));

  // Handle simple passthrough (no filter_complex)
  if (!config.filterComplex) {
    console.log('[FrameFlow] Simple passthrough mode (no filter_complex)');
    return await execPassthrough(config, inputFiles, progressCallback);
  }

  // Parse filter graph
  console.log('[FrameFlow] Raw filter_complex:', config.filterComplex);
  const filterOps = parseFilterComplex(config.filterComplex);
  console.log('[FrameFlow] Parsed filter ops:', filterOps.length, 'chains');

  // Detect composition type
  const hasHstack = filterOps.some(op => op.filters.some(f => f.name === 'hstack'));
  const hasVstack = filterOps.some(op => op.filters.some(f => f.name === 'vstack'));

  if (!hasHstack && !hasVstack) {
    throw new Error(
      'FrameFlow currently only supports hstack/vstack compositions. ' +
      'Use FFmpegWasmEngine for other operations.'
    );
  }

  // Get video inputs (exclude watermark)
  const videoInputs = config.inputs.filter(input =>
    input.filename && !input.filename.includes('watermark')
  );

  if (videoInputs.length !== 2) {
    throw new Error(`FrameFlow hstack/vstack requires exactly 2 video inputs, got ${videoInputs.length}`);
  }

  // Get watermark input if present
  const watermarkInput = config.inputs.find(input =>
    input.filename && input.filename.includes('watermark')
  );

  // Load input files
  const inputBytes = [];
  for (const input of videoInputs) {
    const bytes = inputFiles[input.filename];
    if (!bytes) {
      throw new Error(`Input file not found: ${input.filename}`);
    }
    inputBytes.push(bytes);
  }

  // Load watermark if present
  let watermarkImage = null;
  if (watermarkInput) {
    const wmBytes = inputFiles[watermarkInput.filename];
    if (wmBytes) {
      watermarkImage = await loadImage(wmBytes);
    }
  }

  // Demux both inputs (extract audio from first input only, per -map 0:a?)
  console.log('[FrameFlow] Demuxing inputs...');
  const demuxResults = await Promise.all([
    demuxMP4(inputBytes[0], true),  // First input: extract audio
    demuxMP4(inputBytes[1], false), // Second input: video only
  ]);

  // Get metadata for output sizing
  const meta0 = await getMetadata(inputBytes[0]);
  const meta1 = await getMetadata(inputBytes[1]);
  console.log(`[FrameFlow] Meta0: ${meta0.width}x${meta0.height}, rotation=${meta0.rotation}`);
  console.log(`[FrameFlow] Meta1: ${meta1.width}x${meta1.height}, rotation=${meta1.rotation}`);

  // Find per-video dimensions from crop filters in filter_complex
  // The filter_complex uses: scale=W:-2 (scale to width), crop=W:H:X:Y (crop to final size)
  // The CROP dimensions are the actual per-video dimensions before hstack
  let cropWidth = null;
  let cropHeight = null;
  console.log(`[FrameFlow] Searching ${filterOps.length} filter chains for crop dimensions...`);
  for (const op of filterOps) {
    const inputLabel = op.inputs[0] || '';
    const isVideoInput = inputLabel === '0:v' || inputLabel === '1:v' ||
                         inputLabel.startsWith('tmp') || inputLabel === '';
    const isWatermarkInput = inputLabel === '2:v' || inputLabel.includes('watermark') ||
                             inputLabel.includes('logo');

    for (const filter of op.filters) {
      if (filter.name === 'crop' && isVideoInput && !isWatermarkInput) {
        // crop=width:height:x:y -> params._0=width, _1=height
        const w = filter.params._0 || filter.params.w || filter.params.width;
        const h = filter.params._1 || filter.params.h || filter.params.height;
        console.log(`[FrameFlow] Crop filter: input=${inputLabel}, w=${w}, h=${h}`);
        if (w && w > 0 && h && h > 0) {
          cropWidth = w;
          cropHeight = h;
          console.log(`[FrameFlow] *** Set crop dimensions to ${cropWidth}x${cropHeight}`);
        }
      }
    }
  }

  // Fallback: look for scale filter height if no crop found
  let targetHeight = cropHeight || 480; // Use crop height or default
  if (!cropHeight) {
    console.log(`[FrameFlow] No crop filter found, checking scale filters...`);
    for (const op of filterOps) {
      const inputLabel = op.inputs[0] || '';
      const isVideoInput = inputLabel === '0:v' || inputLabel === '1:v' ||
                           inputLabel.startsWith('tmp') || inputLabel === '';
      const isWatermarkInput = inputLabel === '2:v' || inputLabel.includes('watermark') ||
                               inputLabel.includes('logo');

      for (const filter of op.filters) {
        if (filter.name === 'scale' && isVideoInput && !isWatermarkInput) {
          const h = filter.params._1 || filter.params.h || filter.params.height;
          if (h && h > 0) {
            targetHeight = h;
            console.log(`[FrameFlow] Using scale height: ${targetHeight}`);
          }
        }
      }
    }
  }
  console.log(`[FrameFlow] Final dimensions: cropWidth=${cropWidth}, cropHeight=${cropHeight}, targetHeight=${targetHeight}`);

  // Apply rotation to metadata dimensions if needed (rotation swaps width/height)
  let effectiveWidth0 = meta0.width;
  let effectiveHeight0 = meta0.height;
  let effectiveWidth1 = meta1.width;
  let effectiveHeight1 = meta1.height;

  // Rotation 90 or 270 swaps dimensions
  if (meta0.rotation === 90 || meta0.rotation === 270) {
    effectiveWidth0 = meta0.height;
    effectiveHeight0 = meta0.width;
    console.log(`[FrameFlow] Applied rotation to meta0: ${effectiveWidth0}x${effectiveHeight0}`);
  }
  if (meta1.rotation === 90 || meta1.rotation === 270) {
    effectiveWidth1 = meta1.height;
    effectiveHeight1 = meta1.width;
    console.log(`[FrameFlow] Applied rotation to meta1: ${effectiveWidth1}x${effectiveHeight1}`);
  }

  // Calculate output dimensions using rotation-corrected dimensions
  const fps = 30;
  const aspectRatio0 = effectiveWidth0 / effectiveHeight0;
  const aspectRatio1 = effectiveWidth1 / effectiveHeight1;
  console.log(`[FrameFlow] Aspect ratios: ${aspectRatio0.toFixed(4)}, ${aspectRatio1.toFixed(4)}`);

  // Calculate scaled widths preserving aspect ratio when scaling to targetHeight
  // This is what scaleToHeight does - preserve aspect ratio
  let scaledWidth0 = Math.round(targetHeight * aspectRatio0);
  let scaledWidth1 = Math.round(targetHeight * aspectRatio1);
  console.log(`[FrameFlow] Aspect-ratio preserved widths: ${scaledWidth0}, ${scaledWidth1}`);

  // Final output dimensions after crop (if crop is applied)
  // The crop will reduce widths to cropWidth, so hstack output = 2*cropWidth
  let finalWidth0 = cropWidth || scaledWidth0;
  let finalWidth1 = cropWidth || scaledWidth1;
  console.log(`[FrameFlow] Final widths after crop: ${finalWidth0}, ${finalWidth1}`);

  let outputWidth, outputHeight;
  if (hasHstack) {
    outputWidth = finalWidth0 + finalWidth1;
    outputHeight = cropHeight || targetHeight;
  } else {
    outputWidth = cropWidth || Math.max(scaledWidth0, scaledWidth1);
    outputHeight = (cropHeight || targetHeight) * 2;
  }

  // Ensure even dimensions
  if (outputWidth % 2 !== 0) outputWidth++;
  if (outputHeight % 2 !== 0) outputHeight++;

  console.log(`[FrameFlow] Output dimensions: ${outputWidth}x${outputHeight}`);

  // Create encoder
  const crf = config.outputCrf || 23;
  const { encoder, chunks, close: closeEncoder } = await createEncoder(
    outputWidth, outputHeight, fps, crf
  );

  // Set up processing canvas and frame timing
  const canvas = new OffscreenCanvas(outputWidth, outputHeight);
  const frameDuration = Math.floor(1000000 / fps); // microseconds

  // For original mode, we'll decode all frames upfront
  // For chunked mode, we'll decode per-chunk later
  let frames0 = [];
  let frames1 = [];
  let frameCount = 0;

  // Pre-scale watermark ONCE before the loop (not per-frame!)
  let scaledWatermark = watermarkImage;
  let watermarkX = 24;
  let watermarkY = null; // Will be calculated per-frame if it's an expression
  if (watermarkImage) {
    const overlayOp = filterOps.find(op =>
      op.filters.some(f => f.name === 'overlay')
    );
    if (overlayOp) {
      const overlayFilter = overlayOp.filters.find(f => f.name === 'overlay');
      watermarkX = overlayFilter.params._0 || overlayFilter.params.x || 24;
      const yParam = overlayFilter.params._1 || overlayFilter.params.y || 'main_h-overlay_h-24';
      if (typeof yParam === 'number') {
        watermarkY = yParam;
      }
      // If yParam is a string expression like 'main_h-overlay_h-24', we'll calculate it later
    }

    // Find watermark scale filter (input 2:v) to get target size
    const wmScaleOp = filterOps.find(op =>
      op.inputs.includes('2:v') && op.filters.some(f => f.name === 'scale')
    );
    if (wmScaleOp) {
      const scaleFilter = wmScaleOp.filters.find(f => f.name === 'scale');
      const targetH = scaleFilter.params._1 || scaleFilter.params.h;
      if (targetH && targetH > 0) {
        const aspectRatio = watermarkImage.width / watermarkImage.height;
        const targetW = Math.round(targetH * aspectRatio);
        console.log(`[FrameFlow] Scaling watermark from ${watermarkImage.width}x${watermarkImage.height} to ${targetW}x${targetH}`);

        const wmCanvas = new OffscreenCanvas(targetW, targetH);
        const wmCtx = wmCanvas.getContext('2d');
        wmCtx.drawImage(watermarkImage, 0, 0, targetW, targetH);
        scaledWatermark = wmCanvas;
      }
    }
  }

  // Build processing config for reuse
  const processingConfig = {
    scaledWidth0, scaledWidth1, targetHeight,
    finalWidth0, finalWidth1, cropHeight,
    hasHstack, outputWidth, outputHeight, frameDuration,
    filterOps, scaledWatermark, watermarkX, watermarkY
  };

  // ========================================================================
  // CHUNKED MODE: Process frames in time-aligned chunks for bounded memory
  // ========================================================================
  if (chunked) {
    console.log(`[FrameFlow] Using CHUNKED mode with ${chunkSeconds}s chunks`);

    // Calculate total duration and chunks
    const duration0 = videoInputs[0].duration || (demuxResults[0].videoTrack.duration / demuxResults[0].videoTrack.timescale);
    const duration1 = videoInputs[1].duration || (demuxResults[1].videoTrack.duration / demuxResults[1].videoTrack.timescale);
    const totalDuration = Math.min(duration0, duration1);
    const seek0 = videoInputs[0].seek || 0;
    const seek1 = videoInputs[1].seek || 0;

    const numChunks = Math.ceil(totalDuration / chunkSeconds);
    console.log(`[FrameFlow] Total duration: ${totalDuration.toFixed(2)}s, ${numChunks} chunks`);

    let globalFrameIndex = 0;
    let outputTimestamp = 0;

    for (let chunkIdx = 0; chunkIdx < numChunks; chunkIdx++) {
      if (cancelled) break;

      const chunkStart = chunkIdx * chunkSeconds;
      const chunkEnd = Math.min(chunkStart + chunkSeconds, totalDuration);

      console.log(`[FrameFlow] Processing chunk ${chunkIdx + 1}/${numChunks}: ${chunkStart.toFixed(2)}s - ${chunkEnd.toFixed(2)}s`);

      // Decode this chunk from each video
      const chunk0 = await decodeVideoChunk(demuxResults[0], seek0 + chunkStart, seek0 + chunkEnd);
      const chunk1 = await decodeVideoChunk(demuxResults[1], seek1 + chunkStart, seek1 + chunkEnd);

      console.log(`[FrameFlow] Chunk ${chunkIdx + 1}: decoded ${chunk0.length} + ${chunk1.length} frames`);

      if (chunk0.length === 0 || chunk1.length === 0) {
        console.warn(`[FrameFlow] Empty chunk, skipping`);
        // Clean up any frames
        for (const f of chunk0) f.close();
        for (const f of chunk1) f.close();
        continue;
      }

      // Process frames with timestamp-based pairing
      const frameDurationSec = 1.0 / fps;
      const usedFrames0 = new Set();
      const usedFrames1 = new Set();

      for (let t = chunkStart; t < chunkEnd && !cancelled; t += frameDurationSec) {
        const targetTs = (seek0 + t) * 1_000_000; // microseconds

        // Find closest frame from each chunk
        const left = findClosestFrame(chunk0, targetTs);
        const right = findClosestFrame(chunk1, (seek1 + t) * 1_000_000);

        if (!left || !right) {
          console.warn(`[FrameFlow] Missing frame at t=${t.toFixed(3)}s`);
          continue;
        }

        // Track which frames we've used (they'll be closed after the chunk)
        usedFrames0.add(left);
        usedFrames1.add(right);

        // Process this frame pair
        processFramePair({
          left, right,
          timestamp: outputTimestamp,
          frameIndex: globalFrameIndex,
          encoder, canvas,
          config: processingConfig,
          closeSourceFrames: false  // We'll close all at end of chunk
        });

        outputTimestamp += frameDuration;
        globalFrameIndex++;
      }

      // Close ALL frames in this chunk (even if not used)
      for (const f of chunk0) f.close();
      for (const f of chunk1) f.close();

      // Report progress per chunk
      if (progressCallback) {
        progressCallback((chunkIdx + 1) / numChunks);
      }
    }

    console.log(`[FrameFlow] Chunked processing complete: ${globalFrameIndex} frames`);

    if (cancelled) {
      return null;
    }
  }
  // ========================================================================
  // ORIGINAL MODE: Decode all frames then process (higher memory usage)
  // ========================================================================
  else {
    // Decode frames from both inputs SEQUENTIALLY to avoid resource exhaustion
    // (Parallel decoding can cause "Decoding error" on some devices/browsers)
    console.log('[FrameFlow] Decoding video 0...');
    await decodeVideo(demuxResults[0], videoInputs[0].seek, videoInputs[0].duration, frame => {
      frames0.push(frame);
    });
    console.log(`[FrameFlow] Video 0: ${frames0.length} frames`);

    console.log('[FrameFlow] Decoding video 1...');
    await decodeVideo(demuxResults[1], videoInputs[1].seek, videoInputs[1].duration, frame => {
      frames1.push(frame);
    });
    console.log(`[FrameFlow] Video 1: ${frames1.length} frames`);

    console.log(`[FrameFlow] Decoded ${frames0.length} + ${frames1.length} frames total`);

    if (cancelled) {
      frames0.forEach(f => f.close());
      frames1.forEach(f => f.close());
      return null;
    }

    frameCount = Math.min(frames0.length, frames1.length);

    let lastProcessedIndex = -1;
    for (let i = 0; i < frameCount; i++) {
      if (cancelled) break;
      lastProcessedIndex = i;

      const left = frames0[i];
      const right = frames1[i];
      const timestamp = i * frameDuration;

      // Process this frame pair
      processFramePair({
        left, right,
        timestamp,
        frameIndex: i,
        encoder, canvas,
        config: processingConfig,
        closeSourceFrames: true
      });

      // Report progress
      if (progressCallback) {
        progressCallback((i + 1) / frameCount);
      }
    }

    // Cleanup remaining frames (all frames after last processed index)
    // This covers both: frames we skipped due to cancellation, and excess frames when video lengths differ
    const cleanupStart = lastProcessedIndex + 1;
    for (let i = cleanupStart; i < frames0.length; i++) frames0[i].close();
    for (let i = cleanupStart; i < frames1.length; i++) frames1[i].close();

    if (cancelled) {
      console.log(`[FrameFlow] Cancelled at frame ${lastProcessedIndex}, cleaned up ${frames0.length - cleanupStart} + ${frames1.length - cleanupStart} remaining frames`);
      return null;
    }
  }

  // Finalize encoding
  console.log('[FrameFlow] Finalizing encoding...');
  await closeEncoder();

  // Prepare audio data from first input (per -map 0:a?)
  let audioData = null;
  const firstDemux = demuxResults[0];
  if (firstDemux.audioSamples && firstDemux.audioSamples.length > 0) {
    audioData = {
      samples: firstDemux.audioSamples,
      track: firstDemux.audioTrack,
      codecConfig: firstDemux.audioCodecConfig,
      seekSeconds: videoInputs[0].seek || 0,
      durationSeconds: videoInputs[0].duration,
    };
    console.log(`[FrameFlow] Will mux ${audioData.samples.length} audio samples from first input`);
  }

  // Mux to MP4 (with optional audio)
  console.log('[FrameFlow] Muxing to MP4...');
  const output = muxToMP4(chunks, outputWidth, outputHeight, fps, audioData);

  console.log(`[FrameFlow] Done! Output size: ${output.length} bytes`);

  return output;
}

/**
 * Load an image from bytes.
 */
async function loadImage(bytes) {
  const blob = new Blob([bytes], { type: 'image/png' });
  const bitmap = await createImageBitmap(blob);
  return bitmap;
}

function cancel() {
  cancelled = true;
}

// ============================================================================
// Frame Extraction API (for VideoSessionWebCodecs)
// ============================================================================

/**
 * Demux an MP4 file for later frame extraction.
 * Returns a demux result that can be passed to decodeFrames.
 *
 * @param {Uint8Array} bytes - Video file bytes
 * @returns {Promise<Object>} - Demux result with samples and codec config
 */
async function demux(bytes) {
  console.log(`[FrameFlow.demux] Starting demux of ${bytes.length} bytes`);
  const result = await demuxMP4(bytes);
  console.log(`[FrameFlow.demux] Demuxed: ${result.samples.length} samples, codec=${result.codecConfig?.codec}`);
  return result;
}

/**
 * Decode frames from a demuxed video within a time range.
 * Returns an array of frame objects with RGBA pixel data.
 *
 * @param {Object} demuxResult - Result from demux()
 * @param {number} startSeconds - Start time in seconds
 * @param {number} endSeconds - End time in seconds
 * @param {number} targetFrameRate - Target frame rate for sampling
 * @param {Function} progressCallback - Optional progress callback (0-1)
 * @param {Object} options - Optional settings
 * @param {boolean} options.chunked - Use chunked mode for bounded memory
 * @param {number} options.chunkSeconds - Chunk duration in seconds (default 2)
 * @returns {Promise<Array<{timestamp: number, width: number, height: number, rgba: Uint8Array}>>}
 */
async function decodeFrames(demuxResult, startSeconds, endSeconds, targetFrameRate, progressCallback, options = {}) {
  const { chunked = true, chunkSeconds = 4.0 } = options;
  const { videoTrack, samples, codecConfig } = demuxResult;
  const timescale = videoTrack.timescale;

  console.log(`[FrameFlow.decodeFrames] Input: start=${startSeconds}s, end=${endSeconds}s, rate=${targetFrameRate}fps`);
  console.log(`[FrameFlow.decodeFrames] Video: ${samples.length} samples, timescale=${timescale}`);
  console.log(`[FrameFlow.decodeFrames] Mode: ${chunked ? 'CHUNKED' : 'ORIGINAL'}`);

  // ========================================================================
  // CHUNKED MODE: Decode and extract RGBA per chunk for bounded memory
  // ========================================================================
  if (chunked) {
    const totalDuration = endSeconds - startSeconds;
    const numChunks = Math.ceil(totalDuration / chunkSeconds);
    const frameDuration = 1.0 / targetFrameRate;

    console.log(`[FrameFlow.decodeFrames] Chunked: ${numChunks} chunks of ${chunkSeconds}s`);

    const result = [];
    let canvas = null;
    let ctx = null;

    for (let chunkIdx = 0; chunkIdx < numChunks; chunkIdx++) {
      const chunkStart = startSeconds + (chunkIdx * chunkSeconds);
      const chunkEnd = Math.min(chunkStart + chunkSeconds, endSeconds);

      console.log(`[FrameFlow.decodeFrames] Chunk ${chunkIdx + 1}/${numChunks}: ${chunkStart.toFixed(2)}s - ${chunkEnd.toFixed(2)}s`);

      // Decode this chunk using reusable decodeVideoChunk
      const frames = await decodeVideoChunk(demuxResult, chunkStart, chunkEnd);

      if (frames.length === 0) {
        console.warn(`[FrameFlow.decodeFrames] Empty chunk ${chunkIdx + 1}`);
        continue;
      }

      // Initialize canvas on first frame
      if (!canvas) {
        const firstFrame = frames[0];
        canvas = new OffscreenCanvas(
          firstFrame.displayWidth || firstFrame.codedWidth || 1920,
          firstFrame.displayHeight || firstFrame.codedHeight || 1080
        );
        ctx = canvas.getContext('2d');
      }

      // Sample frames at target rate and extract RGBA
      for (let t = chunkStart; t < chunkEnd; t += frameDuration) {
        const targetTs = t * 1_000_000; // microseconds
        const frame = findClosestFrame(frames, targetTs);

        if (!frame) continue;

        const width = frame.displayWidth || frame.codedWidth;
        const height = frame.displayHeight || frame.codedHeight;

        // Resize canvas if needed
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }

        // Draw frame to canvas and extract RGBA
        ctx.drawImage(frame, 0, 0);
        const imageData = ctx.getImageData(0, 0, width, height);

        result.push({
          timestamp: frame.timestamp,
          width: width,
          height: height,
          rgba: new Uint8Array(imageData.data.buffer),
        });
      }

      // Close ALL frames in this chunk immediately
      for (const frame of frames) {
        frame.close();
      }

      // Report progress per chunk
      if (progressCallback) {
        progressCallback((chunkIdx + 1) / numChunks);
      }
    }

    console.log(`[FrameFlow.decodeFrames] Chunked complete: ${result.length} frames extracted`);
    return result;
  }

  // ========================================================================
  // ORIGINAL MODE: Decode all frames then extract RGBA
  // ========================================================================

  // Calculate time bounds in timescale units
  const startTimestamp = Math.floor(startSeconds * timescale);
  const endTimestamp = Math.floor(endSeconds * timescale);

  // Find the keyframe at or before startTimestamp (required for decoding)
  let keyframeIndex = -1;
  for (let i = 0; i < samples.length; i++) {
    if (samples[i].is_sync && samples[i].dts <= startTimestamp) {
      keyframeIndex = i;
    }
    if (samples[i].dts > startTimestamp) break;
  }

  // If no keyframe found before start, use the first keyframe
  if (keyframeIndex === -1) {
    keyframeIndex = samples.findIndex(s => s.is_sync);
    console.log(`[FrameFlow.decodeFrames] No keyframe before start, using first keyframe at index ${keyframeIndex}`);
  }

  if (keyframeIndex === -1) {
    console.error('[FrameFlow.decodeFrames] No keyframe found in video!');
    return [];
  }

  // Get samples from keyframe to end time
  const relevantSamples = samples.filter((sample, idx) => {
    return idx >= keyframeIndex && sample.dts < endTimestamp;
  });

  console.log(`[FrameFlow.decodeFrames] Processing ${relevantSamples.length} samples (keyframe at index ${keyframeIndex})`);

  if (relevantSamples.length === 0) {
    console.error('[FrameFlow.decodeFrames] No samples in range!');
    return [];
  }

  // Verify first sample is a keyframe
  if (!relevantSamples[0].is_sync) {
    console.error('[FrameFlow.decodeFrames] First sample is not a keyframe!', relevantSamples[0]);
    return [];
  }

  // Calculate frame sampling
  const frameDuration = 1.0 / targetFrameRate;
  const frameTimestamps = [];
  for (let t = startSeconds; t < endSeconds; t += frameDuration) {
    frameTimestamps.push(t);
  }
  console.log(`[FrameFlow.decodeFrames] Will sample ${frameTimestamps.length} frames at ${targetFrameRate}fps`);

  // Decode all samples
  const decodedFrames = [];
  let decodeError = null;

  const decoder = new VideoDecoder({
    output: (frame) => {
      decodedFrames.push(frame);
    },
    error: (e) => {
      console.error('[FrameFlow.decodeFrames] Decoder error:', e);
      decodeError = e;
    },
  });

  // Configure decoder
  console.log('[FrameFlow.decodeFrames] Configuring decoder:', codecConfig.codec);
  console.log('[FrameFlow.decodeFrames] Config:', JSON.stringify({
    codec: codecConfig.codec,
    codedWidth: codecConfig.codedWidth,
    codedHeight: codecConfig.codedHeight,
    hasDescription: !!codecConfig.description,
    descriptionLength: codecConfig.description?.length,
  }));
  console.log('[FrameFlow.decodeFrames] First sample is_sync:', relevantSamples[0]?.is_sync);
  decoder.configure(codecConfig);

  // Feed chunks to decoder (0-60% progress)
  const DECODE_WEIGHT = 0.6;
  const EXTRACT_WEIGHT = 0.4;

  for (let i = 0; i < relevantSamples.length; i++) {
    if (decodeError) {
      console.error('[FrameFlow.decodeFrames] Stopping due to decode error');
      break;
    }

    const sample = relevantSamples[i];
    const chunk = new EncodedVideoChunk({
      type: sample.is_sync ? 'key' : 'delta',
      timestamp: Math.floor(sample.cts * 1000000 / timescale),
      duration: Math.floor(sample.duration * 1000000 / timescale),
      data: sample.data,
    });

    try {
      decoder.decode(chunk);
    } catch (e) {
      console.error(`[FrameFlow.decodeFrames] decode() threw at sample ${i}:`, e);
      throw e;
    }

    // Report decode progress (0-60%)
    if (progressCallback && i % 10 === 0) {
      progressCallback((i / relevantSamples.length) * DECODE_WEIGHT);
    }
  }

  // Report decode complete
  if (progressCallback) {
    progressCallback(DECODE_WEIGHT);
  }

  console.log('[FrameFlow.decodeFrames] All chunks fed, flushing...');

  try {
    await decoder.flush();
  } catch (e) {
    console.error('[FrameFlow.decodeFrames] flush() threw:', e);
    // Clean up frames before throwing
    for (const frame of decodedFrames) {
      frame.close();
    }
    decoder.close();
    throw e;
  }

  decoder.close();
  console.log(`[FrameFlow.decodeFrames] Decoded ${decodedFrames.length} frames`);

  if (decodeError) {
    // Clean up any frames before throwing
    for (const frame of decodedFrames) {
      frame.close();
    }
    throw decodeError;
  }

  if (decodedFrames.length === 0) {
    console.error('[FrameFlow.decodeFrames] No frames decoded!');
    return [];
  }

  // Sort frames by timestamp
  decodedFrames.sort((a, b) => a.timestamp - b.timestamp);

  // Sample frames at target rate and extract RGBA
  const canvas = new OffscreenCanvas(
    decodedFrames[0]?.displayWidth || decodedFrames[0]?.codedWidth || 1920,
    decodedFrames[0]?.displayHeight || decodedFrames[0]?.codedHeight || 1080
  );
  const ctx = canvas.getContext('2d');

  // Sample frames at target rate and extract RGBA (60-100% progress)
  const result = [];
  let frameIdx = 0;
  const totalTargetFrames = frameTimestamps.length;

  for (let i = 0; i < frameTimestamps.length; i++) {
    const targetTime = frameTimestamps[i];
    const targetTimestamp = targetTime * 1000000; // Convert to microseconds

    // Find closest frame
    while (frameIdx < decodedFrames.length - 1 &&
           decodedFrames[frameIdx + 1].timestamp <= targetTimestamp) {
      frameIdx++;
    }

    if (frameIdx >= decodedFrames.length) break;

    const frame = decodedFrames[frameIdx];
    const width = frame.displayWidth || frame.codedWidth;
    const height = frame.displayHeight || frame.codedHeight;

    // Resize canvas if needed
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    // Draw frame to canvas
    ctx.drawImage(frame, 0, 0);

    // Extract RGBA data
    const imageData = ctx.getImageData(0, 0, width, height);

    result.push({
      timestamp: frame.timestamp,
      width: width,
      height: height,
      rgba: new Uint8Array(imageData.data.buffer),
    });

    // Report extraction progress (60-100%)
    if (progressCallback && i % 5 === 0) {
      progressCallback(DECODE_WEIGHT + (i / totalTargetFrames) * EXTRACT_WEIGHT);
    }
  }

  // Report complete
  if (progressCallback) {
    progressCallback(1.0);
  }

  // Close all frames
  for (const frame of decodedFrames) {
    frame.close();
  }

  return result;
}

// ============================================================================
// Exports
// ============================================================================

const FrameFlow = {
  isSupported,
  getMetadata,
  exec,
  cancel,
  // Frame extraction API (for VideoSessionWebCodecs)
  demux,
  decodeFrames,
  // Internal but useful for testing
  parseFilterComplex,
  parseFFmpegArgs,
  operations: {
    scaleFrame,
    cropFrame,
    flipHorizontal,
    flipVertical,
    transpose,
    hstack: hstackFrames,
    vstack: vstackFrames,
    overlay: overlayFrame,
  }
};

// Expose on window
if (typeof window !== 'undefined') {
  window.FrameFlow = FrameFlow;
}

export default FrameFlow;
